import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table} from 'react-bootstrap';
import {useNavigate} from 'react-router-dom';
import {fetchEnhancedSalesReport, fetchFinancialYears} from '../api/api';
import {AccountingScopeSelect, useAccountingScope} from './Accounting/accountingscope';

const money = (value) => Number(value || 0).toLocaleString(undefined, {maximumFractionDigits: 2});
const percent = (value) => `${Number(value || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}%`;

function EnhancedReports() {
    const navigate = useNavigate();
    const scope = useAccountingScope();
    const today = new Date().toISOString().slice(0, 10);
    const [startDate, setStartDate] = useState(`${today.slice(0, 4)}-01-01`);
    const [endDate, setEndDate] = useState(today);
    const [financialYears, setFinancialYears] = useState([]);
    const [selectedFY, setSelectedFY] = useState('');
    const [report, setReport] = useState(null);
    const [salesUser, setSalesUser] = useState('');
    const userSales = report?.user_sales || [];
    const visibleUserSales = userSales.filter(row => !salesUser || String(row.served_by_id ?? 'unassigned') === salesUser);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchFinancialYears()
            .then((data) => setFinancialYears(Array.isArray(data) ? data : data?.results || []))
            .catch(() => setFinancialYears([]));
    }, []);

    const loadReport = useCallback(async (overrides = {}) => {
        if (scope.loadingScope || !scope.storeId) return;
        setLoading(true);
        setError('');
        try {
            const financialYearId = overrides.financial_year_id ?? selectedFY;
            const data = await fetchEnhancedSalesReport({
                start_date: financialYearId ? undefined : startDate,
                end_date: financialYearId ? undefined : endDate,
                financial_year_id: financialYearId || undefined,
                store_id: scope.storeId,
            });
            setReport(data);
            setSalesUser('');
        } catch (requestError) {
            setReport(null);
            setError(requestError?.response?.data?.detail || 'Unable to generate the enhanced report.');
        } finally {
            setLoading(false);
        }
    }, [scope.loadingScope, scope.storeId, selectedFY, startDate, endDate]);

    useEffect(() => {
        if (!scope.loadingScope && scope.storeId) loadReport();
    }, [scope.loadingScope, scope.storeId]); // initial report and store changes only

    const selectFinancialYear = (fy) => {
        setSelectedFY(String(fy.id));
        setStartDate(fy.start_date);
        setEndDate(fy.end_date);
        loadReport({financial_year_id: fy.id});
    };

    const useCustomRange = () => {
        setSelectedFY('');
        loadReport({financial_year_id: ''});
    };

    const highlightCards = report?.highlights ? [
        ['Highest sales day', report.highlights.highest_sales_day, 'sales', 'date', 'success', money],
        ['Lowest trading-day sales', report.highlights.lowest_sales_day, 'sales', 'date', 'warning', money],
        ['Highest-margin day', report.highlights.highest_margin_day, 'margin_percentage', 'date', 'primary', percent],
        ['Lowest-margin day', report.highlights.lowest_margin_day, 'margin_percentage', 'date', 'danger', percent],
        ['Highest-profit day', report.highlights.highest_profit_day, 'profit', 'date', 'success', money],
        ['Lowest-profit day', report.highlights.lowest_profit_day, 'profit', 'date', 'danger', money],
        ['Busiest day', report.highlights.busiest_day, 'order_count', 'date', 'info', money],
        ['Highest sales month', report.highlights.highest_sales_month, 'sales', 'month', 'success', money],
        ['Lowest active sales month', report.highlights.lowest_sales_month, 'sales', 'month', 'warning', money],
        ['Highest-margin month', report.highlights.highest_margin_month, 'margin_percentage', 'month', 'primary', percent],
        ['Lowest-margin month', report.highlights.lowest_margin_month, 'margin_percentage', 'month', 'danger', percent],
        ['Highest-profit month', report.highlights.highest_profit_month, 'profit', 'month', 'success', money],
        ['Lowest-profit month', report.highlights.lowest_profit_month, 'profit', 'month', 'danger', money],
    ] : [];

    return (
        <div className="container-fluid py-4">
            <Button variant="secondary" onClick={() => navigate(-1)} className="mb-3">Back</Button>
            <h1>Enhanced Sales & Margin Report</h1>
            <p className="text-muted">Compare peak, weak, profitable, and low-margin trading periods without loading individual orders.</p>

            {scope.scopeError && <Alert variant="danger">{scope.scopeError}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}

            <Card className="mb-4">
                <Card.Body>
                    <Row className="g-3 align-items-end">
                        {scope.isAdmin && <Col md={3}><AccountingScopeSelect scope={scope} /></Col>}
                        <Col md={3}>
                            <Form.Label>Start date</Form.Label>
                            <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        </Col>
                        <Col md={3}>
                            <Form.Label>End date</Form.Label>
                            <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </Col>
                        <Col md={2}>
                            <Button onClick={useCustomRange} disabled={loading}>Generate</Button>
                        </Col>
                    </Row>
                    {financialYears.length > 0 && (
                        <div className="mt-3">
                            <div className="small text-muted mb-2">Ready-made financial-year reports</div>
                            <div className="d-flex flex-wrap gap-2">
                                {financialYears.map((fy) => (
                                    <Button
                                        key={fy.id}
                                        size="sm"
                                        variant={String(fy.id) === selectedFY ? 'dark' : 'outline-dark'}
                                        onClick={() => selectFinancialYear(fy)}
                                    >
                                        {fy.name}{fy.is_closed ? ' (Closed)' : ' (Open)'}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {loading && <div className="text-center py-5"><Spinner animation="border" /></div>}
            {!loading && report && (
                <>
                    <Alert variant="info">
                        {report.scope?.effective_store_name}: {report.start_date} to {report.end_date}
                    </Alert>
                    <Row className="g-3 mb-4">
                        {[
                            ['Net sales', money(report.summary.sales)],
                            ['Gross profit', money(report.summary.profit)],
                            ['Gross margin', percent(report.summary.margin_percentage)],
                            ['Orders', Number(report.summary.order_count || 0).toLocaleString()],
                            ['Average/trading day', money(report.summary.average_sales_per_trading_day)],
                            ['Zero-sales days', Number(report.summary.zero_sales_days || 0).toLocaleString()],
                        ].map(([label, value]) => (
                            <Col md={4} lg={2} key={label}>
                                <Card className="h-100 text-center"><Card.Body>
                                    <div className="small text-muted">{label}</div>
                                    <div className="fs-5 fw-bold">{value}</div>
                                </Card.Body></Card>
                            </Col>
                        ))}
                    </Row>


                    <h3>Performance highlights</h3>
                    <Row className="g-3 mb-4">
                        {highlightCards.map(([label, row, valueKey, periodKey, variant, formatter]) => (
                            <Col md={6} lg={3} key={label}>
                                <Card className="h-100">
                                    <Card.Body>
                                        <Badge bg={variant}>{label}</Badge>
                                        <div className="fs-5 fw-bold mt-2">{row ? formatter(row[valueKey]) : 'N/A'}</div>
                                        <div className="text-muted">{row?.[periodKey] || 'No trading data'}</div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    <Row className="g-4">
                        <Col lg={7}>
                            <Card><Card.Header>Monthly performance</Card.Header><Card.Body>
                                <Table responsive striped bordered size="sm">
                                    <thead><tr><th>Month</th><th>Sales</th><th>COGS</th><th>Profit</th><th>Margin</th><th>Orders</th></tr></thead>
                                    <tbody>{report.monthly.map((row) => (
                                        <tr key={row.month}><td>{row.month}</td><td>{money(row.sales)}</td><td>{money(row.cogs)}</td><td>{money(row.profit)}</td><td>{percent(row.margin_percentage)}</td><td>{row.order_count}</td></tr>
                                    ))}</tbody>
                                </Table>
                            </Card.Body></Card>
                        </Col>
                        <Col lg={5}>
                            <Card><Card.Header>Top products (before order-level discounts)</Card.Header><Card.Body>
                                <Table responsive striped size="sm">
                                    <thead><tr><th>Product</th><th>Quantity</th><th>Item sales</th></tr></thead>
                                    <tbody>{report.top_products.map((row) => (
                                        <tr key={row.product_id}><td>{row.product__product_name}</td><td>{money(row.quantity)}</td><td>{money(row.sales)}</td></tr>
                                    ))}</tbody>
                                </Table>
                            </Card.Body></Card>
                        </Col>
                        <Col xs={12}>
                    <Card className="mb-4 shadow-sm">
                        <Card.Body>
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                                <div>
                                    <Card.Title>Sales by order-entry user</Card.Title>
                                    <p className="text-muted mb-0">For the generated date range or financial year and store scope. Ranked by final sales, highest first.</p>
                                </div>
                                <Form.Select aria-label="Sales user filter" style={{maxWidth: 260}} value={salesUser} onChange={event => setSalesUser(event.target.value)}>
                                    <option value="">All users</option>
                                    {userSales.map(row => <option key={row.served_by_id ?? 'unassigned'} value={row.served_by_id ?? 'unassigned'}>{row.served_by__username || 'Unassigned / legacy'}</option>)}
                                </Form.Select>
                            </div>
                            <Table responsive striped hover>
                                <thead><tr><th>User</th><th>Orders</th><th>Gross (KES)</th><th>Discounts (KES)</th><th>Final sales (KES)</th><th>Average order (KES)</th></tr></thead>
                                <tbody>
                                    {visibleUserSales.map(row => <tr key={row.served_by_id ?? 'unassigned'}>
                                        <td>{row.served_by__username || 'Unassigned / legacy'}</td>
                                        <td>{row.order_count}</td>
                                        <td>{money(row.gross_sales)}</td>
                                        <td>{money(row.discounts)}</td>
                                        <td>{money(row.net_sales)}</td>
                                        <td>{money(row.order_count ? Number(row.net_sales) / row.order_count : 0)}</td>
                                    </tr>)}
                                    {!visibleUserSales.length && <tr><td colSpan={6} className="text-center text-muted">No orders for this selection.</td></tr>}
                                </tbody>
                            </Table>
                            <div className="small text-muted">Final sales use recorded order amounts, not cash collected. The user filter applies to this table only.</div>
                        </Card.Body>
                    </Card>
                        </Col>
                        <Col lg={6}>
                            <Card><Card.Header>Payment-method mix</Card.Header><Card.Body>
                                <Table responsive striped size="sm"><tbody>{report.payment_methods.map((row) => (
                                    <tr key={row.payment_method}><td>{row.payment_method}</td><td>{row.order_count} orders</td><td>{money(row.sales)}</td></tr>
                                ))}</tbody></Table>
                            </Card.Body></Card>
                        </Col>
                        <Col lg={6}>
                            <Card><Card.Header>Store contribution</Card.Header><Card.Body>
                                <Table responsive striped size="sm"><tbody>{report.stores.map((row) => (
                                    <tr key={row.store_id}><td>{row.store__name}</td><td>{row.order_count} orders</td><td>{money(row.sales)}</td></tr>
                                ))}</tbody></Table>
                            </Card.Body></Card>
                        </Col>
                    </Row>

                    <details className="mt-4">
                        <summary className="fw-bold">Daily performance detail ({report.daily.length} trading days)</summary>
                        <Table responsive striped bordered size="sm" className="mt-2">
                            <thead><tr><th>Date</th><th>Sales</th><th>COGS</th><th>Profit</th><th>Margin</th><th>Orders</th></tr></thead>
                            <tbody>{report.daily.map((row) => (
                                <tr key={row.date}><td>{row.date}</td><td>{money(row.sales)}</td><td>{money(row.cogs)}</td><td>{money(row.profit)}</td><td>{percent(row.margin_percentage)}</td><td>{row.order_count}</td></tr>
                            ))}</tbody>
                        </Table>
                    </details>
                </>
            )}
        </div>
    );
}

export default EnhancedReports;
